import React, { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../config/supabase';
import './AcronymSearch.css';

function AcronymSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [acronyms, setAcronyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [showEmptyWarning, setShowEmptyWarning] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    fetchAcronyms();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAcronyms = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('acronyms')
        .select('*')
        .order('acronym', { ascending: true });

      if (error) throw error;

      setAcronyms(data || []);
    } catch (error) {
      console.error('Error fetching acronyms:', error);
      setError('Failed to load acronyms. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAcronyms = () => {
    let filtered = acronyms;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(item => 
        item.department && item.department.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.acronym.toLowerCase().startsWith(searchLower) ||
        item.full_form.toLowerCase().startsWith(searchLower)
      );
    }

    return filtered;
  };

  const filteredAcronyms = getFilteredAcronyms();

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setShowEmptyWarning(true);
      setTimeout(() => setShowEmptyWarning(false), 3000);
      return;
    }
    
    if (filteredAcronyms.length > 0) {
      setSelectedResult(filteredAcronyms[0]);
      setShowSuggestions(false);
    }
    setShowEmptyWarning(false);
  };

  const handleSelectSuggestion = (acronym) => {
    setSelectedResult(acronym);
    setSearchTerm(acronym.acronym);
    setShowSuggestions(false);
  };

  const handleInputChange = (value) => {
    setSearchTerm(value);
    setShowSuggestions(value.length > 0);
    setShowEmptyWarning(false);
    if (!value) {
      setSelectedResult(null);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedResult(null);
    setShowSuggestions(false);
  };

  const departments = useMemo(() => {
    const uniqueDepts = new Set();
    acronyms.forEach(item => {
      if (item.department) {
        uniqueDepts.add(item.department);
      }
    });
    return ['All', ...Array.from(uniqueDepts).sort()];
  }, [acronyms]);

  const popularAcronyms = useMemo(() => {
    let filtered = acronyms;
    if (selectedCategory !== 'All') {
      filtered = acronyms.filter(item => 
        item.department && item.department.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    const shuffled = [...filtered].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 9);
  }, [acronyms, selectedCategory]);

  if (loading) {
    return (
      <div className="acronym-search">
        <div className="hero-section">
          <h1 className="main-title">Frequently Asked Acronyms</h1>
          <p className="main-subtitle">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="acronym-search">
      <div className="hero-section">
        <h1 className="main-title">Frequently Asked Acronyms</h1>
        <p className="main-subtitle">
          Instant access to professional business acronyms. Search, learn, and contribute to the most
          comprehensive business terminology database.
        </p>

        <div className="category-filters">
          {departments.map((department) => (
            <button
              key={department}
              className={`category-btn ${selectedCategory === department ? 'active' : ''}`}
              onClick={() => setSelectedCategory(department)}
            >
              {department}
            </button>
          ))}
        </div>

        <div className="search-container" ref={searchRef}>
          <div className="search-box-wrapper">
            <div className="search-input-group">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="search-input"
                placeholder="Search acronyms..."
                value={searchTerm}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              {searchTerm && (
                <button className="clear-btn" onClick={handleClearSearch}>
                  ✕
                </button>
              )}
            </div>

            {showSuggestions && filteredAcronyms.length > 0 && !selectedResult && (
              <div className="suggestions-dropdown">
                <div className="suggestions-header">
                  {filteredAcronyms.length} result{filteredAcronyms.length !== 1 ? 's' : ''} found
                </div>
                {filteredAcronyms.slice(0, 5).map((item) => (
                  <div
                    key={item.id}
                    className="suggestion-item"
                    onClick={() => handleSelectSuggestion(item)}
                  >
                    <span className="suggestion-badge">{item.acronym}</span>
                    <div className="suggestion-content">
                      <div className="suggestion-title">{item.full_form}</div>
                      {item.description && (
                        <div className="suggestion-description">{item.description}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showSuggestions && filteredAcronyms.length === 0 && searchTerm && (
              <div className="suggestions-dropdown no-results">
                <div className="no-results-content">
                  <span className="no-results-icon">🔍</span>
                  <h3 className="no-results-title">No Results Found</h3>
                  <p className="no-results-text">
                    We couldn't find any acronyms matching "<strong>{searchTerm}</strong>"
                  </p>
                  <p className="no-results-hint">
                    Try adjusting your search or browse our Most Used Acronyms below
                  </p>
                </div>
              </div>
            )}

            {showEmptyWarning && (
              <div className="suggestions-dropdown empty-warning">
                <div className="no-results-content">
                  <span className="no-results-icon">⚠️</span>
                  <h3 className="no-results-title">Please Enter a Search Term</h3>
                  <p className="no-results-text">
                    The search box is empty. Please type an acronym or term to search.
                  </p>
                </div>
              </div>
            )}

            <button className="search-btn" onClick={handleSearch}>
              <span className="btn-icon">🔍</span>
              Search
            </button>
          </div>
        </div>
      </div>

      {selectedResult && (
        <div className="result-modal">
          <div className="result-card">
            <div className="result-header">
              <div className="result-badge">{selectedResult.acronym}</div>
              <h2 className="result-title">{selectedResult.full_form}</h2>
              {selectedResult.department && (
                <span className="result-tag">{selectedResult.department} Acronym</span>
              )}
            </div>
            {selectedResult.description && (
              <div className="result-description">
                <p>{selectedResult.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!selectedResult && (
        <div className="popular-section">
          <h2 className="popular-title">Most Used Acronyms</h2>
          <p className="popular-subtitle">Essential business terms every professional should know</p>
          <div className="popular-grid">
            {popularAcronyms.map((item) => (
              <div
                key={item.id}
                className="popular-card"
                onClick={() => handleSelectSuggestion(item)}
              >
                <div className="popular-badge">{item.acronym}</div>
                <div className="popular-name">{item.full_form}</div>
                {item.description && (
                  <div className="popular-desc">{item.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}
    </div>
  );
}

export default AcronymSearch;

